import React from 'react';

import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { Wizard, WizardStep } from 'src/hooks/useWizard';
import { ProposalLifecycleEvent } from 'src/lib/graphqlTypes/types';

import {
  CreateBuildSubscriptionModal,
  CreateBuildSubscriptionState,
} from '../createBuildSubscriptionModal/CreateBuildSubscriptionModal';

import {
  AddEventsModal,
  AddEventsMode,
  AddEventsState,
} from './addEventsModal/AddEventsModal';
import {
  ChooseNotificationTypeModal,
  ChooseNotificationTypeState,
} from './chooseNotificationTypeModal/ChooseNotificationTypeModal';
import {
  CreateProposalSubscriptionModal,
  CreateProposalSubscriptionState,
} from './createProposalSubscriptionModal/CreateProposalSubscriptionModal';
import {
  CreateRegistrySubscriptionModal,
  CreateRegistrySubscriptionState,
} from './createRegistrySubscriptionModal/CreateRegistrySubscriptionModal';
import {
  CreateScheduledSummaryModal,
  CreateScheduledSummaryState,
} from './createScheduledSummaryModal/CreateScheduledSummaryModal';
import { CreateSlackChannelModal } from './createSlackChannelModal/CreateSlackChannelModal';
import { CreateWebhookChannelModal } from './createWebhookChannelModal/CreateWebhookChannelModal';
import {
  FinishNotificationModal,
  FinishNotificationState,
} from './finishNotificationModal/FinishNotificationModal';
import {
  SelectChannelAndVariantState,
  SelectVariantAndChannelModal,
} from './selectVariantAndChannelModal/SelectVariantAndChannelModal';

type AddNotificationWizardState = ChooseNotificationTypeState &
  SelectChannelAndVariantState &
  CreateRegistrySubscriptionState &
  CreateScheduledSummaryState &
  CreateBuildSubscriptionState &
  CreateProposalSubscriptionState &
  FinishNotificationState &
  AddEventsState;
interface Props {
  isOpen: boolean;
  onClose: () => void;
  __test?: {
    initialStepId?: string;
    initialState?: AddNotificationWizardState;
  };
}

export enum Steps {
  ChooseNotificationType = 'choose-notification-type',
  SelectVariantAndChannel = 'select-variant-and-channel',
  CreateChannel = 'create-channel',
  CreateSubscription = 'create-subscription',
  AddEvents = 'add-events',
  Finish = 'finish-notification',
}

export function AddNotificationWizard({ isOpen, onClose, __test }: Props) {
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    accountId: currentAccountId as string,
    skip: !currentAccountId,
  });

  const canViewWebhooks = !!currentPlan.currentPlan?.webhooks;

  const wizardSteps = React.useMemo(
    (): MinLengthArray<1, WizardStep<AddNotificationWizardState>> => [
      {
        id: Steps.ChooseNotificationType,
        name: 'Choose notification type',
        content: ({ state, mergeState, goToNextStep, reset }) => (
          <ChooseNotificationTypeModal
            isOpen={isOpen}
            state={state}
            onTypeChange={(notificationType) =>
              mergeState({ notificationType })
            }
            onComplete={() => goToNextStep()}
            onClose={() => {
              reset();
              onClose();
            }}
          />
        ),
      },
      {
        id: Steps.SelectVariantAndChannel,
        name: 'Choose variant & channel',
        content: ({
          state,
          mergeState,
          setCurrentStep,
          goToPreviousStep,
          reset,
        }) => (
          <SelectVariantAndChannelModal
            isOpen={isOpen}
            state={state}
            canViewWebhooks={canViewWebhooks}
            onCreateChannelChange={(createNewChannel) =>
              mergeState({
                createNewChannel,
                newChannelType: undefined,
                targetChannel: undefined,
              })
            }
            onExistingChannelChange={(existingChannel) =>
              mergeState({
                targetChannel: existingChannel,
                newChannelType: undefined,
              })
            }
            onVariantChange={(variant) => mergeState({ variant })}
            onNewChannelTypeChange={(newChannelType) =>
              mergeState({ newChannelType, targetChannel: undefined })
            }
            onPrevious={() => {
              if (state?.notificationType === 'ProposalLifecycleSubscription') {
                mergeState({
                  variant: undefined,
                  createNewChannel: undefined,
                  newChannelType: undefined,
                  targetChannel: undefined,
                });
              }
              goToPreviousStep();
            }}
            onComplete={() => {
              if (state?.createNewChannel) {
                setCurrentStep(Steps.CreateChannel);
              } else if (
                state?.notificationType === 'ProposalLifecycleSubscription'
              ) {
                setCurrentStep(Steps.AddEvents);
              } else {
                setCurrentStep(Steps.CreateSubscription);
              }
            }}
            onClose={() => {
              reset();
              onClose();
            }}
          />
        ),
      },
      {
        id: Steps.CreateChannel,
        name: 'Create new channel',
        content: ({
          state,
          goToPreviousStep,
          goToNextStep,
          setCurrentStep,
          mergeState,
          reset,
        }) =>
          state?.newChannelType === 'WebhookChannel' && canViewWebhooks ? (
            <CreateWebhookChannelModal
              state={state}
              isOpen={isOpen}
              onChannelCreated={(newChannel) => {
                mergeState({ targetChannel: newChannel });
                if (
                  state?.notificationType === 'ProposalLifecycleSubscription'
                ) {
                  goToNextStep();
                } else {
                  setCurrentStep(Steps.CreateSubscription);
                }
              }}
              onPreviousClick={() => goToPreviousStep()}
              onClose={() => {
                reset();
                onClose();
              }}
            />
          ) : (
            <CreateSlackChannelModal
              isOpen={isOpen}
              onChannelCreated={(newChannel) => {
                mergeState({ targetChannel: newChannel });
                if (
                  state?.notificationType === 'ProposalLifecycleSubscription'
                ) {
                  goToNextStep();
                } else {
                  setCurrentStep(Steps.CreateSubscription);
                }
              }}
              onPreviousClick={() => goToPreviousStep()}
              onClose={() => {
                reset();
                onClose();
              }}
            />
          ),
      },
      {
        id: Steps.AddEvents,
        name: 'Add events',
        content: ({ state, mergeState, setCurrentStep, reset }) => (
          <AddEventsModal
            mode={AddEventsMode.ADD}
            isOpen={isOpen}
            state={state}
            onCheckboxChange={(events: ProposalLifecycleEvent[]) =>
              mergeState({ events })
            }
            onPrevious={() => {
              setCurrentStep(Steps.SelectVariantAndChannel);
            }}
            onComplete={() => {
              setCurrentStep(Steps.CreateSubscription);
            }}
            onClose={() => {
              reset();
              onClose();
            }}
          />
        ),
      },
      {
        id: Steps.CreateSubscription,
        name: 'Create subscription',
        content: ({
          state,
          mergeState,
          setCurrentStep,
          goToNextStep,
          reset,
        }) =>
          state?.notificationType === 'SchemaPublishSubscription' ? (
            <CreateRegistrySubscriptionModal
              isOpen={isOpen}
              state={state}
              onSubscriptionCreated={(newSubscriptionId) => {
                mergeState({ newSubscriptionId });
                goToNextStep();
              }}
              onClose={() => {
                reset();
                onClose();
              }}
            />
          ) : state?.notificationType === 'ScheduledSummary' ? (
            <CreateScheduledSummaryModal
              isOpen={isOpen}
              state={state}
              onSubscriptionCreated={(newSubscriptionId) => {
                mergeState({ newSubscriptionId });
                goToNextStep();
              }}
              onPrevious={() => {
                if (state?.targetChannel) {
                  // if we just created a channel, go back to the beginning
                  // but make it look like we selected the channel we just
                  // created
                  mergeState({
                    createNewChannel: false,
                    newChannelType: undefined,
                  });
                }
                setCurrentStep(Steps.SelectVariantAndChannel);
              }}
              onClose={() => {
                reset();
                onClose();
              }}
            />
          ) : state?.notificationType === 'ProposalLifecycleSubscription' ? (
            <CreateProposalSubscriptionModal
              isOpen={isOpen}
              state={state}
              onSubscriptionCreated={(newSubscriptionId) => {
                mergeState({ newSubscriptionId });
                goToNextStep();
              }}
              onClose={() => {
                reset();
                onClose();
              }}
            />
          ) : (
            <CreateBuildSubscriptionModal
              isOpen={isOpen}
              state={state}
              onSubscriptionCreated={(newSubscriptionId) => {
                mergeState({ newSubscriptionId });
                goToNextStep();
              }}
              onClose={() => {
                reset();
                onClose();
              }}
            />
          ),
      },
      {
        id: Steps.Finish,
        name: 'Finish notification setup',
        content: ({ state, reset }) => (
          <FinishNotificationModal
            isOpen={isOpen}
            state={state}
            onClose={() => {
              reset();
              onClose();
            }}
          />
        ),
      },
    ],
    [isOpen, canViewWebhooks, onClose],
  );
  return <Wizard<AddNotificationWizardState> {...__test} steps={wizardSteps} />;
}
